import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/common/Button';
import InfoContent from '../../../components/common/InfoContent';
import Modal from '../../../components/common/Modal';
import UpdateCardModal from '../../../components/common/UpdateCardModal';
import Config from '../../../Config';
import { useCartContext } from '../../../context/CartContext';
import { useGlobalContext } from '../../../context/GlobalContext';
import { usePayment } from '../../../context/PaymentContext';
import ClientId from '../services/ClientId';
import PaymentFormLocal from '../services/PaymentFormLocal';
import TaxId from '../services/TaxtId';
import AccessibleContent from '../../../utils/AccessibleContent';
import { validateTaxId, validateVAT } from '../../../utils/validationClient';
import { formatDateTime } from '../../../utils/DateUtils';
import { stripHtml } from '../../../utils/utils';
import validator from 'validator';

import VAT from '../services/Vat';
import DateInputWithMask from '../../../components/widgets/DateInputWithMask';

const Step2 = ({ step, selectedStore, lang }) => {
  const { t } = useTranslation();
  const { formatAmounts, store, user, validateClientId, checkProfessionalDiscount, updateProfessionalDiscount, goToStep } = useGlobalContext();
  const {
    cart,
    totalAmounts,
    totalAmountsAfterDiscount,
    totalPort,
    totalFinal,
    deleteRequestCartItem,
    selectOption,
    selectDelivery,
    isDeliveryDisabled,
    filterDeliveriesByWeight,
    generateCartItemsByAddress,
    generateCartItemsByAmount,
    calcCartTotals,
    haveDiscount,
    cartDiscountCondition,
    discountType,
    cartMinNotReached,
    cartAmountMinNotReached,
    promoCode,
    promoDiscount,
    promoError,
    promoLoading,
    applyPromoCode,
    removePromoCode,
  } = useCartContext();
  const { createOrder, checkTransaction, handlePayment, pspType, paymentHtml, loading: orderLoading } = usePayment();
  const [infoField, setInfoField] = useState('');
  const [verifiedDiscount, setVerifiedDiscount] = useState(null);
  const [isCheckingDiscount, setIsCheckingDiscount] = useState(false);
  const [initialDiscount, setInitialDiscount] = useState(null);
  const [promoCodeInput, setPromoCodeInput] = useState(promoCode || '');
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [modalUpdateOpen, setModalUpdateOpen] = useState(false);
  const [modalUpdateType, setModalUpdateType] = useState(null); // 'update' ou 'copy'
  const [cardToUpdate, setCardToUpdate] = useState(null);
  const [cardIndex, setCardIndex] = useState(null);
  // Cart states
  const orderCardByAmountInCart = store?.orderByAmountInCart || false;
  const professionalUser = store.professionalUser;
  const [userData, setUserData] = useState(() => {
    const baseData = {
      firstname: '',
      lastname: '',
      email: '',
      confEmail: '',
      phone: '',
      accepteCGV: false,
      accepteNL: false,
      allowTD: false,
      extraOptins: {},
      // KYC fields
      kyc_address_1: '',
      kyc_address_2: '',
      kyc_address_3: '',
      kyc_address_4: '',
      kyc_postalCode: '',
      kyc_city: '',
      kyc_state: '',
      kyc_country: '',
      kyc_civility: '',
      kyc_birthdate: '',
      // Invoice fields
      isGettingInvoiceActivated: store.sending_invoice_activated === 2,
      taxId: '',
      vat: '',
      companyTransmitter: '',
      addressTransmitter: '',
      addressComplementTransmitter: '',
      addressComplementTransmitter1: '',
      addressComplementTransmitter2: '',
      postalCode: '',
      city: '',
      country: store.country || ''
    };
    if (professionalUser) {
      return {
        ...baseData,
        firstname: professionalUser.firstname || '',
        lastname: professionalUser.lastname || '',
        email: professionalUser.email || '',
        confEmail: professionalUser.email || '',
      };
    }

    return baseData;
  });

  const cartSenderItem = !professionalUser ? cart?.items?.find(item => item.emailSender) : null;
  useEffect(() => {
    if (cartSenderItem) {
      setUserData(prev => ({
        ...prev,
        firstname: cartSenderItem.firstNameSender || prev.firstname,
        lastname: cartSenderItem.lastNameSender || prev.lastname,
        email: cartSenderItem.emailSender || prev.email,
        confEmail: cartSenderItem.emailSender || prev.confEmail
      }));
    }
  }, [cartSenderItem?.emailSender, cartSenderItem?.firstNameSender, cartSenderItem?.lastNameSender]);

  const [formErrors, setFormErrors] = useState({
    isRequiredMsg: '',
    isEmaildMsg: '',
    isEqualToMsg: '',
    isValidDeliveries: '',
    isValidProIdentification: '',
    extraOptinsRequired: '',
    exceptionMsg: ''
  });

  // Helper pour vérifier si un champ KYC doit être affiché
  // thresholdPrice est la condition d'affichage : afficher si totalAmounts >= thresholdPrice
  const shouldShowKycField = (kycConfig) => {
    if (!store.kycEnabled || !kycConfig) return false;
    return totalAmounts >= kycConfig.thresholdPrice;
  };

  // Helper pour vérifier si un champ KYC est requis (validation)
  // required concerne uniquement les champs affichés
  const isKycFieldRequired = (kycConfig) => {
    if (!store.kycEnabled || !kycConfig) return false;
    // Obligatoire SI affiché ET required = true
    return shouldShowKycField(kycConfig) && kycConfig.required === true;
  };

  const showDateSend = (item) => item.sendDate && !item.isSelfDestination;

  const handleDeleteClick = (key) => {
    setItemToDelete(key);
    setModalType('delete');
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Gestion spéciale pour les extraOptins
    if (name.startsWith('extraOptin_')) {
      const optinKey = name.replace('extraOptin_', '');
      setUserData(prev => ({
        ...prev,
        extraOptins: {
          ...prev.extraOptins,
          [optinKey]: checked
        }
      }));
    } else {
      const trimFields = ['email', 'confEmail', 'proIdentification'];
      const finalValue = type === 'checkbox' ? checked : (trimFields.includes(name) ? value.replace(/\s/g, '') : value);
      setUserData(prev => ({
        ...prev,
        [name]: finalValue
      }));
    }
  };

  const handleProIdentificationBlur = async (e) => {
    const proIdentification = e.target.value;

    if (!proIdentification || !store.isProSignboard) {
      return;
    }
    setIsCheckingDiscount(true);
    try {
      const discount = await checkProfessionalDiscount(proIdentification);

      if (discount !== null && discount > 0) {
        setVerifiedDiscount(discount);
        updateProfessionalDiscount(discount);
      } else {
        setVerifiedDiscount(null);
        if (initialDiscount !== null && initialDiscount !== undefined) {
          updateProfessionalDiscount(initialDiscount);
        }
      }
    } catch (error) {
      setVerifiedDiscount(null);
      if (initialDiscount !== null && initialDiscount !== undefined) {
        updateProfessionalDiscount(initialDiscount);
      }
    } finally {
      setIsCheckingDiscount(false);
    }
  };

  useEffect(() => {
    if (cart.items) {
      if (selectedStore === 'physical') {
        generateCartItemsByAddress(store);
      } else if (orderCardByAmountInCart) {
        generateCartItemsByAmount(store);
      }
      calcCartTotals(store);
    }
  }, [cart.items]);

  useEffect(() => {
    if (store.discount?.conditions?.[0]?.value !== undefined) {
      setInitialDiscount(store.discount.conditions[0].value);
    }
  }, [store.discount?.conditions]);


  const handleUpdateClick = (card, index) => {
    setModalUpdateType('update');
    setCardToUpdate(card);
    setCardIndex(index);
    setModalUpdateOpen(true);
  };
  const handleCopyClick = (card, index) => {
    setModalUpdateType('copy');
    setCardToUpdate(card);
    setCardIndex(index);
    setModalUpdateOpen(true);
  };
  useEffect(() => {
    if (professionalUser && professionalUser.discount) {

      setUserData(prev => ({
        ...prev,
        firstname: professionalUser.firstname || '',
        lastname: professionalUser.lastname || '',
        email: professionalUser.email || '',
        confEmail: professionalUser.email || '',
      }));
    }
  }, [professionalUser]);
  useEffect(() => {
    if (pspType === 'local-form') {
      // Ajouter jQuery si nécessaire
      if (!window.jQuery) {
        const jqueryScript = document.createElement('script');
        jqueryScript.src = "https://code.jquery.com/jquery-3.6.0.min.js";
        jqueryScript.async = true;
        jqueryScript.onload = () => {
          console.log("jQuery loaded");
        };
        document.head.appendChild(jqueryScript);
      }
    }
  }, [pspType]);
  const getStateName = (state) => {
    if (!state?.names) return '';
    return (
      state.names[lang] ||
      state.names[store.default_language] ||
      state.names[Object.keys(state.names)[0]]
    ) + ` (${state.code})`;
  };
  const handleShowInfo = (field) => {
    setInfoField(field);
  };

  const handleHideInfo = () => {
    setInfoField('');
  };

  const validateForm = () => {
    const formattedCards = cart.items.map(item => {
      const baseCard = {
        card: item.card,
        signboardId: store.id,
        visualId: item.visualId,
        message: item.message || "",
        amount: item.amount,
        firstNameReceiver: item.firstNameReceiver,
        lastNameReceiver: item.lastNameReceiver,
        emailReceiver: item.isSelfDestination ? userData.email : item.emailReceiver,
        isSelfDestination: item.isSelfDestination,
        sendDate: item.sendDate,
        sendDateTmp: item.sendDateTmp,
        cgv: true,
        receiveInfos: userData.accepteNL ? "1" : "0",
        qty: item.qty || 1,
        momentSend: item.momentSend,
        cardLanguage: item.cardLanguage || lang,
        isGettingInvoiceActivated: userData.isGettingInvoiceActivated,
        isGettingLivstickVideo: false,
        firstNameSender: userData.firstname,
        lastNameSender: userData.lastname,
        emailSender: userData.email,
        phoneSender: userData.phone,
        clientId: userData.clientId || null
      };

    })
    const errors = {};
    let isValid = true;
    const isProWithDiscount = professionalUser && professionalUser.discount;

    if (cartMinNotReached()) {
      errors.cartMin = t("Le nombre minimum de cartes n'est pas atteint pour valider votre panier.", {
        count: store.cartItemsCountMin
      });
      isValid = false;
    }

    if (cartAmountMinNotReached()) {
      errors.cartAmountMin = t("Le montant minimum n'est pas atteint pour valider votre panier.", {
        amount: formatAmounts(store.cartAmountMin)
      });
      isValid = false;
    }

    if (store.isProSignboard) {
      if (!userData.proIdentification) {
        errors.proIdentification = t("L'identifiant de société est obligatoire");
        isValid = false;
      }
      if (!userData.accepteCGV) {
        errors.accepteCGV = t("Veuillez accepter les conditions générales");
        isValid = false;
      }

      if (store.extraOptins && Array.isArray(store.extraOptins)) {
        const requiredOptinsNotAccepted = store.extraOptins.filter(option =>
          option.required && !userData.extraOptins[option.key]
        );

        if (requiredOptinsNotAccepted.length > 0) {
          errors.extraOptinsRequired = t("Veuillez accepter toutes les conditions obligatoires");
          isValid = false;
        }
      }
    } else {
      if (!isProWithDiscount) {
        if (!userData.firstname) {
          errors.firstname = t("Le prénom est obligatoire");
          isValid = false;
        } else if (userData.firstname.length > 25) {
          errors.firstname = t("Votre prénom ne doit pas dépasser 25 caractères");
          isValid = false;
        }

        if (!userData.lastname) {
          errors.lastname = t("Le nom est obligatoire");
          isValid = false;
        } else if (userData.lastname.length > 25) {
          errors.lastname = t("Votre nom ne doit pas dépasser 25 caractères");
          isValid = false;
        }

        const basicEmailRegex = /^[^\s<>()[\]\\,;:"']+@[^\s<>()[\]\\,;:"']+\.[^\s<>()[\]\\,;:"']+$/;

        if (!userData.email?.trim()) {
          errors.email = t("L'email est obligatoire");
          isValid = false;
        } else {
          const email = userData.email.trim();
          if (!basicEmailRegex.test(email) || !validator.isEmail(email)) {
            errors.email = t("Veuillez saisir un email valide");
            isValid = false;
          }
        }

        if (!userData.confEmail) {
          errors.confEmail = t("La confirmation d'email est obligatoire");
          isValid = false;
        } else if (userData.email !== userData.confEmail) {
          errors.confEmail = t("Les emails renseignés sont différents");
          isValid = false;
        }

        if (store.phone_enabled || isKycFieldRequired(store.kycs?.buyer_phone)) {
          if (!userData.phone) {
            errors.phone = t("Le numéro de téléphone est obligatoire");
            isValid = false;
          } else if (store.phone_regex && !new RegExp(store.phone_regex).test(userData.phone)) {
            errors.phone = t("Format de téléphone invalide");
            isValid = false;
          }
        }
      }

      if (store.codeTaxId || isKycFieldRequired(store.kycs?.buyer_taxid)) {
        const isTaxIdOptional = store.tax_id_optional ||
          (store.tax_id_position === 1 && !userData.isGettingInvoiceActivated);

        const isRequired = isKycFieldRequired(store.kycs?.buyer_taxid) || !isTaxIdOptional;

        const isFromOldCondition = store.codeTaxId;

        if (!isTaxIdOptional || (userData.taxId && userData.taxId.trim() !== '')) {
          if (!userData.taxId && isRequired) {
            errors.taxId = t("L'identifiant fiscal est obligatoire");
            isValid = false;
          } else if (userData.taxId && userData.taxId.trim() !== '') {
            if (isFromOldCondition) {
              const isTaxIdValid = validateTaxId(
                userData.taxId,
                store.codeTaxId,
                { isOptional: isTaxIdOptional }
              );

              if (!isTaxIdValid) {
                errors.taxId = t("Le format de la TVA n'est pas valide");
                isValid = false;
              }
            }
          }
        }
      }

      if (store.vatEnabled) {
        const isVatOptional = store.vat_optional ||
          (store.vat_position === 1 && !userData.isGettingInvoiceActivated);

        if (!isVatOptional && !userData.vat) {
          errors.vat = t("Le numéro de TVA est obligatoire");
          isValid = false;
        } else if (userData.vat && userData.vat.trim() !== '') {
          if (store.validateVat && !validateVAT(userData.vat, userData.country)) {
            errors.vat = t("Le format du numéro de TVA n'est pas valide");
            isValid = false;
          }
        }
      }

      if (store.clientIdType) {
        const isClientIdOptional = store.isClientIdOptional === true;

        if (!isClientIdOptional || (userData.clientId && userData.clientId.trim() !== '')) {
          if (!userData.clientId && !isClientIdOptional) {
            errors.clientId = t("Le numéro de carte IKEA est obligatoire");
            isValid = false;
          }
          else if (userData.clientId && userData.clientId.trim() !== '') {
            const isClientIdValid = validateClientId(userData.clientId);

            if (!isClientIdValid) {
              errors.clientId = t("Numéro de carte IKEA invalide");
              isValid = false;
            }
          }
        }
      }

      if (!isProWithDiscount && isKycFieldRequired(store.kycs?.buyer_address)) {
        if (!userData.kyc_address_1) {
          errors.kyc_address_1 = t("L'adresse est obligatoire");
          isValid = false;
        }
        if (!userData.kyc_city) {
          errors.kyc_city = t("La ville est obligatoire");
          isValid = false;
        }
        if (!userData.kyc_country) {
          errors.kyc_country = t("Le pays est obligatoire");
          isValid = false;
        }
        if (isKycFieldRequired(store.kycs?.buyer_zipcode) || isKycFieldRequired(store.kycs?.buyer_address)) {
          if (!userData.kyc_postalCode) {
            errors.kyc_postalCode = t("Le code postal est obligatoire");
            isValid = false;
          }
        }
      }

      if (!isProWithDiscount && isKycFieldRequired(store.kycs?.buyer_civility)) {
        if (!userData.kyc_civility) {
          errors.kyc_civility = t("La civilité est obligatoire");
          isValid = false;
        }
      }

      if (!isProWithDiscount && isKycFieldRequired(store.kycs?.buyer_birthdate)) {
        if (!userData.kyc_birthdate) {
          errors.kyc_birthdate = t("La date de naissance est obligatoire");
          isValid = false;
        }
      }

      if (userData.isGettingInvoiceActivated) {
        if (!isProWithDiscount && store.sending_invoice_activated === 3 && !userData.companyTransmitter) {
          errors.companyTransmitter = t("Le nom de l'entreprise est obligatoire");
          isValid = false;
        }

        if (!isProWithDiscount) {
          if (!userData.addressTransmitter) {
            errors.addressTransmitter = t("L'adresse de facturation est obligatoire");
            isValid = false;
          }
          if (!userData.postalCode) {
            errors.postalCode = t("Le code postal est obligatoire");
            isValid = false;
          }
          if (!userData.city) {
            errors.city = t("La ville est obligatoire");
            isValid = false;
          }
          if (!userData.country) {
            errors.country = t("Le pays est obligatoire");
            isValid = false;
          }
        }
      }

      if (!userData.accepteCGV) {
        errors.accepteCGV = t("Veuillez accepter les conditions générales");
        isValid = false;
      }

      if (store.extraOptins && Array.isArray(store.extraOptins)) {
        const requiredOptinsNotAccepted = store.extraOptins.filter(option =>
          option.required && !userData.extraOptins[option.key]
        );

        if (requiredOptinsNotAccepted.length > 0) {
          errors.extraOptinsRequired = t("Veuillez accepter toutes les conditions obligatoires");
          isValid = false;
        }
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  const FieldError = ({ error, id }) => {
    if (!error) return null;

    return (
      <div className="field-error" id={`error-${id}`} role="alert">
        <span className="error-icon" aria-hidden="true">⚠️</span>
        <span className="error-message">{error}</span>
      </div>
    );
  };

  useEffect(() => {
    if (pspType === 'local-form' && paymentHtml) {
      // Injecter le HTML
      const container = document.getElementById('payment-local-container');
      if (container) {
        container.innerHTML = paymentHtml;

        // Injecter les scripts dynamiquement
        const scripts = container.getElementsByTagName('script');
        Array.from(scripts).forEach(oldScript => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          newScript.appendChild(document.createTextNode(oldScript.innerHTML));
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });
      }
    }
  }, [pspType, paymentHtml]); // Déclenché quand le type PSP ou le HTML change

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const formattedCards = cart.items.map(item => {
        const baseCard = {
          card: item.card,
          signboardId: store.id,
          visualId: item.visualId,
          message: item.message || "",
          amount: item.amount,
          firstNameReceiver: item.firstNameReceiver,
          lastNameReceiver: item.lastNameReceiver,
          emailReceiver: item.isSelfDestination ? (item.emailSender || userData.email) : item.emailReceiver,
          isSelfDestination: item.isSelfDestination,
          sendDate: item.sendDate,
          sendDateTmp: item.sendDateTmp,
          cgv: true,
          receiveInfos: userData.accepteNL ? "1" : "0",
          qty: item.qty || 1,
          momentSend: item.momentSend,
          cardLanguage: item.cardLanguage || lang,
          isGettingInvoiceActivated: userData.isGettingInvoiceActivated,
          isGettingLivstickVideo: false,
          firstNameSender: item.firstNameSender || userData.firstname,
          lastNameSender: item.lastNameSender || userData.lastname,
          emailSender: item.emailSender || userData.email,
          phoneSender: userData.phone,
          clientId: userData.clientId || null,
          taxId: userData.taxId || null,
          vat: userData.vat,

        };

        if (shouldShowKycField(store.kycs?.buyer_taxid)) {
          baseCard.taxIdSender = userData.taxId || null;
        }

        if (selectedStore === 'physical') {
          baseCard.physical = {
            address_1: item.physical?.address_1,
            address_2: item.physical?.address_2,
            address_3: item.physical?.address_3,
            address_4: item.physical?.address_4,
            city: item.physical?.city,
            zip: item.physical?.zip,
            country: item.physical?.country,
            state: item.physical?.state,
            state_code: item.physical?.state_code
          };

          const address = [
            item.firstNameReceiver,
            item.lastNameReceiver,
            item.physical?.address_1,
            item.physical?.address_2,
            item.physical?.address_3,
            item.physical?.address_4,
            item.physical?.zip,
            item.physical?.city,
            item.physical?.country
          ].join('_').replace(/[^a-zA-Z0-9_]/g, '-');

          if (cart.delivery && cart.delivery[address]) {
            baseCard.delivery = cart.delivery[address].id;
          }

          if (cart.option && cart.option[address]) {
            const activeOptionKey = Object.keys(cart.option[address]).find(key =>
              cart.option[address][key] === true
            );

            if (activeOptionKey) {

              if (store?.physical_store?.card_options && Array.isArray(store.physical_store.card_options)) {
                const optionIndex = parseInt(activeOptionKey, 10);
                if (!isNaN(optionIndex) && optionIndex < store.physical_store.card_options.length) {
                  const option = store.physical_store.card_options[optionIndex];

                  if (option.anchor) {
                    baseCard.option = option.anchor;
                  }
                }
              }
            }
          }
        }

        if (shouldShowKycField(store.kycs?.buyer_address)) {
          const addressParts = [
            userData.kyc_address_1,
            userData.kyc_address_2,
            userData.kyc_address_3,
            userData.kyc_address_4
          ].filter(part => part && part.trim() !== '');

          const fullAddress = addressParts.join(', ');

          Object.assign(baseCard, {
            address1Sender: userData.kyc_address_1,
            address2Sender: userData.kyc_address_2,
            address3Sender: userData.kyc_address_3,
            address4Sender: userData.kyc_address_4,
            postalCodeSender: userData.kyc_postalCode,
            citySender: userData.kyc_city,
            stateSender: userData.kyc_state,
            countrySender: userData.kyc_country,
            addressSender: fullAddress,
          });
        }

        if (shouldShowKycField(store.kycs?.buyer_civility)) {
          Object.assign(baseCard, {
            civilitySender: userData.kyc_civility,
          });
        }

        if (shouldShowKycField(store.kycs?.buyer_birthdate)) {
          Object.assign(baseCard, {
            birthdateSender: userData.kyc_birthdate,
          });
        }

        if (userData.isGettingInvoiceActivated) {
          Object.assign(baseCard, {
            addressTransmitter: userData.addressTransmitter,
            addressComplementTransmitter: userData.addressComplementTransmitter,
            postalCode: userData.postalCode,
            city: userData.city,
            country: userData.country,
            companyTransmitter: userData.companyTransmitter
          });
        }




        return baseCard;
      });

      const acceptedExtraOptins = userData.extraOptins ?
        Object.keys(userData.extraOptins).filter(key => userData.extraOptins[key]) : [];

      // Infos device requises par Lyra
      const deviceInfoValues = {
        javaEnabled: typeof navigator.javaEnabled === 'function' ? navigator.javaEnabled() : false,
        language: navigator.language,
        colorDepth: String(window.screen.colorDepth),
        screenHeight: window.screen.height,
        screenWidth: window.screen.width,
        timeZoneOffset: new Date().getTimezoneOffset()
      };
      const clientDeviceInfo = {};
      if (store.PSP_needs_client_device_info) {
        const requestedFields = store.PSP_client_device_info_fields || [];
        for (const field of requestedFields) {
          if (field in deviceInfoValues) {
            clientDeviceInfo[field] = deviceInfoValues[field];
          }
        }
      }

      const proDiscount = store.professionalUser?.discount || store.proSignboardDiscount || 0;
      const hasDiscount = haveDiscount && (
        (proDiscount > 0) ||
        (store.discount && (cartDiscountCondition || cart.items.some(item => item.discountCondition))) ||
        promoDiscount
      );

      const orderData = {
        api_key: Config.api.key,
        store: selectedStore,
        cards: formattedCards,
        lng: lang,
        amex: cart.useAmex || false,
        client: null,
        proIdentification: store.isProSignboard ? userData.proIdentification : null,
        mobile: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? '1' : '0',
        extraOptins: acceptedExtraOptins,
        discountApplied: hasDiscount ? 1 : 0,
        promotional_code: promoDiscount ? promoCode : null,
        clientDeviceInfo
      };

      const orderResult = await createOrder(orderData);
      if (orderResult) {
        handlePayment(orderResult.result.Data, orderResult.result.htmlInfo);
      }
    } catch (error) {
      setFormErrors(prev => ({
        ...prev,
        exceptionMsg: error.message
      }));
    }
  };

  const handleShowBankTransferInfo = () => {
    setModalType('bankTransfer');
    setShowModal(true);
  };

  useEffect(() => {
    if (cart.items.length === 0 && (step === 2 || step == "payment")) {
      goToStep(1);
    }
  }, [cart.items.length, step]);


  useEffect(() => {
    if (step === 'payment') {
      const queryParams = new URLSearchParams(window.location.search);
      if (queryParams.has('PAYID')) {
        checkTransaction(
          queryParams.get('reference'),
          queryParams.get('status'),
          Object.fromEntries(queryParams.entries())
        );
      }
    }
  }, [step, checkTransaction]);

  if (step !== 2 && step !== 'payment') return null;

  return (
    <div className={`step-2 store-${selectedStore} ${step === 'payment' ? 'step-payment' : ''}`}>
          <div className="checkoutTabs" role="tablist" aria-label={t('Étapes de commande')}>
            <button
              type="button"
              role="tab"
              aria-selected={step !== 'payment'}
              className={`checkoutTab ${step !== 'payment' ? 'active' : ''}`}
              onClick={() => { if (step === 'payment') goToStep(2); }}
            >
              {t('Détail')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={step === 'payment'}
              className={`checkoutTab ${step === 'payment' ? 'active' : ''}`}
              disabled={step !== 'payment'}
            >
              {t('Paiement')}
            </button>
          </div>
          <p className="checkoutSubtitle">
            {step === 'payment'
              ? t('Veuillez renseigner vos informations de paiement.')
              : t('Veuillez vérifier le détail de votre commande.')}
          </p>
          <div className="left-column-container" aria-labelledby="section-heading">
            <h2 id="section-heading" className="sr-only">{t('Détails de votre commande')}</h2>
            <div className="cardSection2">
              <div className="cartItems">
                {/* En-tête du panier */}
                <div className="cartItemsHead">
                  <div className="itemData panelHeader">
                    <div className="row">
                      <span className="rfCartHead">
                        {cart.items?.length || 0}{' '}
                        {selectedStore === 'physical' ? t('carte cadeau') : t('e-carte cadeau')}
                      </span>
                      {!orderCardByAmountInCart && (
                        <>
                          <span className="rfCartQte" >
                            <div className="flex">
                              <span>{t('Quantité :')}</span>
                              <span className="mainColor typoBold">{cart.items?.length || 0}</span>
                            </div>
                          </span>
                          <span className="rfCartTotal">
                            <div className="flex">
                              {t('Total')} :&nbsp;
                              <span className="mainColor typoBold">{formatAmounts(totalAmounts, 2)}</span>
                            </div>
                          </span>
                          {haveDiscount && cartDiscountCondition && (
                            <div className="cartDiscountRow">
                              <span className="cartDiscountInfo">
                                {discountType === 'discount' ? t('Remise :') : t('Bonus :')}
                                <span className="mainColor typoBold">
                                  &nbsp;{discountType === 'discount' ? '-' : '+'}{cartDiscountCondition.valueType === 'percent'
                                    ? `${cartDiscountCondition.value}%`
                                    : formatAmounts(cartDiscountCondition.value, 2)}
                                </span>
                              </span>
                              <span className="cartDiscountTotal">
                                {discountType === 'discount' ? t('Total dû') : t('Valeur réelle')} :&nbsp;
                                <span className="mainColor typoBold">{formatAmounts(totalAmountsAfterDiscount, 2)}</span>
                              </span>
                            </div>
                          )}
                          {selectedStore === 'physical' && (
                            <div className="cartDiscountRow">
                              <span className="cartDiscountInfo">
                                {t('Frais de port')} :&nbsp;
                                <span className="mainColor typoBold">{formatAmounts(totalPort, 2)}</span>
                              </span>
                              <span className="cartDiscountTotal">
                                {t('Total dû')} :&nbsp;
                                <span className="mainColor typoBold">{formatAmounts(totalFinal, 2)}</span>
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {store.isProSignboard && store.discount && verifiedDiscount === null && !isCheckingDiscount && (
                    <span className="infoRemise typoBold">
                      {t('La remise pourra être mise à jour après la saisie de votre identifiant')}
                    </span>
                  )}
                  {store.isProSignboard && isCheckingDiscount && (
                    <span className="infoRemise typoBold">
                      {t('Vérification de la remise en cours...')}
                    </span>
                  )}
                </div>

                {selectedStore === 'digital' && !orderCardByAmountInCart && cart.items?.map((item, index) => (
                  <div key={index} className="rfCartItem cartItemRow">
                    <div className="itemPreviewBox">
                      <img className="itemPreview" src={item.card || 'placeholder.jpg'} alt={t('Aperçu de la carte cadeau')} />
                    </div>
                    <div className="itemData">
                      <div className="rfPrice typoBold">
                        <div>{formatAmounts(item.amount, item.amount % 1 ? 2 : 0)}</div>
                        {item.discountCondition && discountType === 'abundance' && item.amount !== item.amountAfterDiscount && (
                          <div className="alignCenter discount-abundance">
                            {item.discountCondition.valueType === 'percent' ? (
                              <>
                                <div>
                                  + {item.discountCondition.value}%<br />
                                  {t('Valeur réelle')} :
                </div>
                                <div className="rfPrice">{formatAmounts(item.amountAfterDiscount, 2)}</div>
                              </>
                            ) : (
                                <>
                                  <div>
                                    + {formatAmounts(item.discountCondition.value, 2)}<br />
                                    {t('Valeur réelle')} :
                </div>
                                  <div className="rfPrice">{formatAmounts(item.amountAfterDiscount, 2)}</div>
                                </>
                              )}
                          </div>
                        )}
                        {item.discountCondition && discountType === 'discount' && item.amount !== item.amountAfterDiscount && (
                          <div className=" discount-abundance">
                            {item.discountCondition.valueType === 'percent' ? (
                              <>
                                <div>
                                  - {item.discountCondition.value}%<br />
                                  {t('Montant après remise')} :
                </div>
                                <div className="rfPrice">{formatAmounts(item.amountAfterDiscount, 2)}</div>
                              </>
                            ) : (
                                <>
                                  <div>
                                    - {formatAmounts(item.discountCondition.value, 2)}<br />
                                    {t('Montant après remise')} :
                </div>
                                  <div className="rfPrice">{formatAmounts(item.amountAfterDiscount, 2)}</div>
                                </>
                              )}
                          </div>
                        )}
                      </div>
                      {(item.firstNameReceiver || item.lastNameReceiver) && (
                        <div className="rfSender iName">
                          {item.firstNameReceiver} {item.lastNameReceiver}
                        </div>
                      )}
                      {!item.isSelfDestination ? (
                        <div className="rfSender">{item.emailReceiver}</div>
                      ) : (
                        <div className="rfSender">{t('Remise en main propre')}</div>
                      )}
                      {item.message && (
                        <div className="cardComment">{item.message}</div>
                      )}
                      {showDateSend(item) && (
                        <div className="rfDate sendBadge">
                          {(() => {
                            const timeData = formatDateTime.formatDisplayTime(item.sendDate, store.format_time);
                            return (
                              <>
                                {t('Envoi le')} {formatDateTime.formatDisplayDate(item.sendDate, store.format_date)} {t('à')}
                                {timeData.time}<span className="timezone-small">{timeData.timezone}</span>
                              </>
                            );
                          })()}
                        </div>
                      )}
                      <a
                        href="#"
                        className="deleteItem"

                        onClick={(e) => { e.preventDefault(); handleDeleteClick(index); }}
                        aria-label={t('Supprimer cet article')}

                      >
                        <i className="icon-trash" aria-hidden="true"></i>
                        <span className="sr-only">{t('Supprimer cet article')}</span>
                      </a>
                    </div>
                  </div>
                ))}

                {/* Affichage groupé par montant pour digital */}
                {selectedStore === 'digital' && orderCardByAmountInCart && Object.entries(cart.itemsByAmount || {}).map(([amount, cards]) => (
                  <div key={amount} className="rfCartItem cartByAmount">
                    <img className="itemPreview" src={cards[0].card || 'placeholder.jpg'} alt={t('Aperçu de la carte cadeau')} />
                    <div className="itemData">
                      <div className="rfPrice">
                        <div>{formatAmounts(cards[0].amount, cards[0].amount % 1 ? 2 : 0)}</div>
                        {cards[0].discountCondition && discountType === 'abundance' && (
                          <div className="alignCenter discount-abundance">
                            {cards[0].discountCondition.valueType === 'percent' ? (
                              <>
                                <div>
                                  + {cards[0].discountCondition.value}%<br />
                                  {t('Valeur réelle')} :
                </div>
                                <div className="rfPrice">
                                  {formatAmounts(cards[0].amountAfterDiscount, 2)}
                                </div>
                              </>
                            ) : (
                                <>
                                  <div>
                                    + {formatAmounts(cards[0].discountCondition.value, 2)}<br />
                                    {t('Valeur réelle')} :
                </div>
                                  <div className="rfPrice">
                                    {formatAmounts(cards[0].amountAfterDiscount, 2)}
                                  </div>
                                </>
                              )}
                          </div>
                        )}
                        {cards[0].discountCondition && discountType === 'discount' && (
                          <div className="alignCenter discount-abundance">
                            {cards[0].discountCondition.valueType === 'percent' ? (
                              <>
                                <div>
                                  - {cards[0].discountCondition.value}%<br />
                                  {t('Montant après remise')} :
                </div>
                                <div className="rfPrice">
                                  {formatAmounts(cards[0].amountAfterDiscount, 2)}
                                </div>
                              </>
                            ) : (
                                <>
                                  <div>
                                    - {formatAmounts(cards[0].discountCondition.value, 2)}<br />
                                    {t('Montant après remise')} :
                </div>
                                  <div className="rfPrice">
                                    {formatAmounts(cards[0].amountAfterDiscount, 2)}
                                  </div>
                                </>
                              )}
                          </div>
                        )}
                      </div>
                      <div className="iQuantity">{t('Quantity: ')} {cards.length}</div>
                    </div>
                    <div style={{ clear: 'both' }}></div>
                    <div className="cards">
                      {cards.map((item, idx) => (
                        <div key={idx}>
                          <img className="itemPreview" src={item.card || 'placeholder.jpg'} alt={t('Aperçu de la carte cadeau')} />
                          <div className="itemData">
                            {!store.isProSignboard && (
                              !item.isSelfDestination ? (
                                <div className="rfSender">
                                  {t('Envoyée à :')} {item.emailReceiver}
                                </div>
                              ) : (
                                  <>
                                    <div className="rfSender">
                                      {t('Envoyée à :')} {item.firstNameReceiver} {item.lastNameReceiver}
                                    </div>
                                    <div className="rfSender">{t('Remise en main propre')}</div>
                                  </>
                                )
                            )}

                            {showDateSend(item) && (
                              <div className="rfDate sendBadge">
                                {(() => {
                                  const timeData = formatDateTime.formatDisplayTime(item.sendDate, store.format_time);
                                  return (
                                    <>
                                      {t('Envoi le')} {formatDateTime.formatDisplayDate(item.sendDate, store.format_date)} {t('à')}
                                      {timeData.time}<span className="timezone-small">{timeData.timezone}</span>
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                          <div>
                            {!store.isProSignboard && (
                              <a
                                href="#"
                                className="updateCardBtn"
                                onClick={(e) => { e.preventDefault(); handleUpdateClick(item, item.key); }}
                                aria-label={t('Modifier cet article')}

                              >
                                <i className="fa fa-pen" aria-hidden="true"></i>
                                <span className="sr-only">{t('Modifier cet article')}</span>
                              </a>
                            )}
                            <a
                              href="#"
                              className="deleteItem"
                              onClick={(e) => { e.preventDefault(); handleDeleteClick(item.key); }}
                              aria-label={t('Supprimer cet article')}

                            >
                              <i className="icon-trash" aria-hidden="true"></i>
                              <span className="sr-only">{t('Supprimer cet article')}</span>
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                    {cards.length < 10 && !store.isProSignboard && (
                      <div className="copyCard">
                        <span className="infoAnotherCard">{t('You want to send this card to more people ?')}</span>
                        <a
                          href="#"
                          className="rfBtn buyOther btnAnotherCard"
                          onClick={(e) => { e.preventDefault(); handleCopyClick(cards[0], cards[0].key); }}
                        >
                          {t('Add another card')}
                        </a>
                      </div>
                    )}
                  </div>
                ))}

                {/* Affichage pour le store physique */}
                {selectedStore === 'physical' && Object.entries(cart.itemsByAddress || {}).map(([keyCart, cards]) => (
                  <div key={keyCart} className="rfCartItem cartPhysical mainColor">
                    {cards.map((item, idx) => (
                      <div key={idx}>
                        <img className="itemPreview" src={item.card || 'placeholder.jpg'} alt={t('Aperçu de la carte cadeau')} />
                        <div className="itemData">
                          <div className="rfPrice">{formatAmounts(item.amount)}</div>
                          <a
                            href="#"
                            className="deleteItem"
                            onClick={(e) => { e.preventDefault(); handleDeleteClick(item.key); }}
                            aria-label={t('Supprimer cet article')}

                          >
                            <i className="icon-trash" aria-hidden="true"></i>
                            <span className="sr-only">{t('Supprimer cet article')}</span>
                          </a>
                        </div>
                        <div className="cardComment"><p>{item.message}</p></div>
                      </div>
                    ))}
                    <div className="cartAddress">
                      <span className="title">{t('Adresse de livraison')}:</span>
                      <span>
                        {cards[0].firstNameReceiver} {cards[0].lastNameReceiver}<br />
                        {cards[0].physical?.address_1}<br />
                        {cards[0].physical?.address_2 && (<>{cards[0].physical.address_2}<br /></>)}
                        {cards[0].physical?.address_3 && (<>{cards[0].physical.address_3}<br /></>)}
                        {cards[0].physical?.zip} {cards[0].physical?.city}<br />
                        {cards[0].physical?.state && (<>{cards[0].physical.state}<br /></>)}
                        {cards[0].physical?.country}
                      </span>
                    </div>
                    <div className="cartOptions">
                      {store.physical_store?.card_options?.length > 0 && (
                        <div className="title">{t("Options")}:</div>
                      )}
                      {store.physical_store?.card_options
                        ?.filter(option => option.card_count_min <= cards.length && option.card_count_max >= cards.length)
                        .map((option, keyOption) => (
                          <div key={keyOption} className="cartOption">
                            <span>
                              <a className="mainColor" href={`/options#${option.anchor}`}>
                                {option.name}
                              </a>
                            </span>
                            <span>{formatAmounts(option.price, 2)}</span>
                            <span>
                              <input
                                type="checkbox"
                                onChange={() => selectOption(keyCart, keyOption)}
                                checked={cart.option?.[keyCart]?.[keyOption] || false}
                                name={`option[${keyCart}][${keyOption}]`}
                                value="1"
                              />
                            </span>
                          </div>
                        ))}
                    </div>
                    <div className="cartDelivery">
                      <label className="title" htmlFor={`delivery_${keyCart}`}>
                        {t('Choisir le mode de livraison')}
                      </label>
                      <select
                        id={`delivery_${keyCart}`}
                        onChange={(e) => selectDelivery(keyCart, e.target.value)}
                        required
                        name={`delivery[${keyCart}]`}
                        value={cart.delivery[keyCart]?.id || ''}
                      >
                        {store.physical_store?.deliveries
                          ?.filter(delivery => filterDeliveriesByWeight(keyCart)(delivery))
                          .map(delivery => (
                            <option
                              key={delivery.id}
                              value={delivery.id}
                              disabled={isDeliveryDisabled(delivery, keyCart)}
                            >
                              {delivery.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                ))}

                <div className="checkoutTotals">
                  <div className="totalRow subTotal">
                    <span>{t('Sous-total')}</span>
                    <span>{formatAmounts(totalAmounts, 2)}</span>
                  </div>
                  {haveDiscount && totalAmounts !== totalAmountsAfterDiscount && (
                    <div className="rfDiscount discountRow">
                      <span>{discountType === 'discount' ? t('Réduction') : t('Bonus')}</span>
                      <span>
                        {discountType === 'discount' ? '-' : '+'}
                        {formatAmounts(Math.abs(totalAmountsAfterDiscount - totalAmounts), 2)}
                      </span>
                    </div>
                  )}
                  {selectedStore === 'physical' && (
                    <div className="totalRow shippingRow">
                      <span>{t('Frais de port')}</span>
                      <span>{formatAmounts(totalPort, 2)}</span>
                    </div>
                  )}
                  <div className="totalRow grandTotal">
                    <span>{t('Total')}</span>
                    <span>{formatAmounts(totalFinal, 2)}</span>
                  </div>
                </div>
              </div>
            </div>
            <AccessibleContent
              htmlContent={store.mentionLegale[lang]}
              className="bottomSection2-step2 mainColor"
            />
          </div>
          {/* Section "Vos coordonnées" */}
          <div className="orderSection">
            {orderCardByAmountInCart && (
              <section className="orderRecap" aria-labelledby="order-summary-title">
                <h2 id="order-summary-title" className="sr-only">{t('Récapitulatif de la commande')}</h2>
                <span className="rfCartTotal">
                  <div className="flex">
                    {t('Total')} :&nbsp;
                    <span className="mainColor">{formatAmounts(totalAmounts, 2)}</span>
                  </div>
                </span>
                <span className="rfCartQte">
                  <div className="flex">
                    {t('Quantité :')}
                    <span className="mainColor">&nbsp;{cart.items?.length || 0}</span>
                  </div>
                </span>
                {haveDiscount && cartDiscountCondition && (
                  <div className="cartDiscountRow" style={{ paddingLeft: 0 }}>
                    <span className="cartDiscountInfo">
                      {discountType === 'discount' ? t('Remise :') : t('Bonus :')}
                      <span className="mainColor">
                        &nbsp;{discountType === 'discount' ? '-' : '+'}{cartDiscountCondition.valueType === 'percent'
                          ? `${cartDiscountCondition.value}%`
                          : formatAmounts(cartDiscountCondition.value, 2)}
                      </span>
                    </span>
                    <span className="cartDiscountTotal">
                      {discountType === 'discount' ? t('Total dû') : t('Valeur réelle')} :&nbsp;
                      <span className="mainColor">{formatAmounts(totalAmountsAfterDiscount, 2)}</span>
                    </span>
                  </div>
                )}
                {selectedStore === 'physical' && (
                  <div className="cartDiscountRow" style={{ paddingLeft: 0 }}>
                    <span className="cartDiscountInfo">
                      {t('Frais de port')} :&nbsp;
                      <span className="mainColor">{formatAmounts(totalPort, 2)}</span>
                    </span>
                    <span className="cartDiscountTotal">
                      {t('Total dû')} :&nbsp;
                      <span className="mainColor">{formatAmounts(totalFinal, 2)}</span>
                    </span>
                  </div>
                )}
              </section>)}
            {step !== 'payment' && (
              <form method="post" name="formCreateOrder" id="formCreateOrder" noValidate onSubmit={handleSubmit}>
                <div className="mainCol">
                  <div className="whiteBox" role="region" aria-labelledby="contact-info-heading">
                    <div className="stepBox">
                      <div className="formBox2">
                        {(!cartSenderItem ||
                          store.isProSignboard ||
                          store.phone_enabled ||
                          shouldShowKycField(store.kycs?.buyer_phone) ||
                          shouldShowKycField(store.kycs?.buyer_address) ||
                          shouldShowKycField(store.kycs?.buyer_civility) ||
                          shouldShowKycField(store.kycs?.buyer_birthdate) ||
                          !!store.clientIdType) && (
                          <h2 className="rfTitle alignCenter" id="contact-info-heading" style={{ padding: 0 }}>{t('Vos coordonnées')}</h2>
                        )}
                        <div className="fieldsGroup">
                          {!store.isProSignboard && !cartSenderItem && (
                            <>
                              <div className="formField">
                                <div className="formControl">
                                  <input
                                    type="text"
                                    name="firstname"
                                    id="firstname"
                                    required
                                    value={userData.firstname}
                                    onChange={handleInputChange}
                                    placeholder={t('Prénom') + '*'}
                                    aria-label={t("Prénom")}
                                    className={formErrors.firstname ? 'error' : ''}
                                    aria-invalid={formErrors.firstname ? 'true' : 'false'}
                                    aria-describedby={formErrors.firstname ? 'error-firstname' : undefined}
                                    disabled={professionalUser && professionalUser.discount}
                                  />
                                </div>
                                {formErrors.firstname && <FieldError error={formErrors.firstname} id="firstname" />}
                              </div>
                              <div className="formField">
                                <div className="formControl">
                                  <input
                                    type="text"
                                    name="lastname"
                                    id="lastname"
                                    value={userData.lastname}
                                    onChange={handleInputChange}
                                    required
                                    placeholder={t('Nom') + '*'}
                                    aria-label={t("Nom")}
                                    className={formErrors.lastname ? 'error' : ''}
                                    aria-invalid={formErrors.lastname ? 'true' : 'false'}
                                    aria-describedby={formErrors.lastname ? 'error-lastname' : undefined}
                                    disabled={professionalUser && professionalUser.discount}
                                  />
                                </div>
                                {formErrors.lastname && <FieldError error={formErrors.lastname} id="lastname" />}
                              </div>
                              <div className="formField">
                                <div className="formControl hasInfo">
                                  <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    value={userData.email}
                                    onChange={handleInputChange}
                                    required
                                    placeholder={t('Email') + '*'}
                                    aria-label={t("Email")}
                                    className={formErrors.email ? 'error' : ''}
                                    aria-invalid={formErrors.email ? 'true' : 'false'}
                                    aria-describedby={formErrors.email ? 'error-email' : undefined}
                                    disabled={professionalUser && professionalUser.discount}
                                  />
                                  <InfoContent
                                    id="forEmailTransmitter"
                                    infoField={infoField}
                                    showInfo={handleShowInfo}
                                    context="emailStep2"
                                    hideInfo={handleHideInfo}
                                    infoText={
                                      <>
                                        <h3 className="typoMedium">
                                          {userData.isSelfDestination
                                            ? t('La carte cadeau vous sera envoyée par email')
                                            : t('Une copie de la carte cadeau vous sera envoyée par email')}
                                        </h3>
                                        <p>{t('Veuillez privilégier une adresse personnelle car les serveurs professionnels peuvent filtrer les informations commerciales')}</p>
                                      </>
                                    }
                                    ariaLabelShow={t("Informations sur l'email expéditeur")}
                                    ariaLabelHide={t("Fermer les informations")}
                                  />
                                </div>
                                {formErrors.email && <FieldError error={formErrors.email} id="email" />}
                              </div>
                              <div className="formField">
                                <div className="formControl">
                                  <input
                                    type="email"
                                    name="confEmail"
                                    id="confEmail"
                                    value={userData.confEmail}
                                    onChange={handleInputChange}
                                    placeholder={t('Email confirmation') + '*'}
                                    aria-label={t("Email confirmation")}
                                    className={formErrors.confEmail ? 'error' : ''}
                                    aria-invalid={formErrors.confEmail ? 'true' : 'false'}
                                    aria-describedby={formErrors.confEmail ? 'error-confEmail' : undefined}
                                    disabled={professionalUser && professionalUser.discount}
                                  />
                                </div>
                                {formErrors.confEmail && <FieldError error={formErrors.confEmail} id="confEmail" />}
                              </div>
                            </>
                          )}
                          {!store.isProSignboard && store.clientIdType && (
                            <ClientId
                              userData={userData}
                              setUserData={setUserData}
                              formErrors={formErrors}
                              setFormErrors={setFormErrors}
                            />
                          )}

                          {/* Champ téléphone avec condition */}
                          {(store.phone_enabled || shouldShowKycField(store.kycs?.buyer_phone)) && (
                            <div className="formField">
                              <div className="formControl">
                                <input
                                  type="text"
                                  name="phone"
                                  id="phone"
                                  value={userData.phone}
                                  onChange={handleInputChange}
                                  required={store.phone_enabled || isKycFieldRequired(store.kycs?.buyer_phone)}
                                  placeholder={store.phone_placeholder + (store.phone_enabled || isKycFieldRequired(store.kycs?.buyer_phone) ? '*' : '')}
                                  aria-label={t("phone")}
                                  className={formErrors.phone ? 'error' : ''}
                                  aria-invalid={formErrors.phone ? 'true' : 'false'}
                                  aria-describedby={formErrors.phone ? 'error-phone' : undefined}
                                  disabled={professionalUser && professionalUser.discount}
                                />
                              </div>
                              {formErrors.phone && <FieldError error={formErrors.phone} id="phone" />}
                            </div>
                          )}


                          {store.isProSignboard && (
                            <div className="formField">
                              <div className="formControl hasInfo">
                                <input
                                  type="text"
                                  name="proIdentification"
                                  id="proIdentification"
                                  value={userData.proIdentification || ''}
                                  onChange={handleInputChange}
                                  onBlur={handleProIdentificationBlur}
                                  required
                                  placeholder={t('Votre identifiant de société') + '*'}
                                  aria-label={t("Votre identifiant de société")}
                                  className={formErrors.proIdentification ? 'error' : ''}
                                  aria-invalid={formErrors.proIdentification ? 'true' : 'false'}
                                  aria-describedby={formErrors.proIdentification ? 'error-proIdentification' : undefined}
                                />
                                <InfoContent
                                  id="forProIdentification"
                                  infoField={infoField}
                                  showInfo={handleShowInfo}
                                  hideInfo={handleHideInfo}
                                  infoText={
                                    <>
                                      <h5 className="typoMedium">
                                        {t(store.proIdentificationType)}
                                      </h5>
                                      <p>
                                        {t('Note: vous devez au préalable avoir communiqué cette information au service Frizbee')}
                                      </p>
                                    </>
                                  }
                                  ariaLabelShow={t("Informations sur l'identifiant de société")}
                                  ariaLabelHide={t("Fermer les informations")}
                                />
                              </div>
                              {formErrors.proIdentification && <FieldError error={formErrors.proIdentification} id="proIdentification" />}
                            </div>
                          )}
                          {/* KYC Fields */}
                          {shouldShowKycField(store.kycs?.buyer_address) && (
                            <>
                              <div className="formField" id="row_buyer_address_1">
                                <div className="formControl">
                                  <input
                                    type="text"
                                    name="kyc_address_1"
                                    id="buyer_kyc_address_1"
                                    value={userData.kyc_address_1}
                                    onChange={handleInputChange}
                                    required={isKycFieldRequired(store.kycs?.buyer_address)}
                                    placeholder={t('Adresse ligne 1') + (isKycFieldRequired(store.kycs?.buyer_address) ? '*' : '')}
                                    aria-label={t("Adresse ligne 1")}
                                    className={formErrors.kyc_address_1 ? 'error' : ''}
                                    aria-invalid={formErrors.kyc_address_1 ? 'true' : 'false'}
                                    aria-describedby={formErrors.kyc_address_1 ? 'error-kyc_address_1' : undefined}
                                  />
                                </div>
                                {formErrors.kyc_address_1 && <FieldError error={formErrors.kyc_address_1} id="kyc_address_1" />}
                              </div>

                              <div className="formField" id="row_buyer_address_2">
                                <div className="formControl">
                                  <input
                                    type="text"
                                    name="kyc_address_2"
                                    id="buyer_kyc_address_2"
                                    value={userData.kyc_address_2}
                                    onChange={handleInputChange}
                                    placeholder={t('Complément adresse ligne 2')}
                                    aria-label={t("Complément adresse ligne 2")}

                                  />
                                </div>
                              </div>

                              {store.nbAddressLine >= 3 && (
                                <div className="formField" id="row_buyer_address_3">
                                  <div className="formControl">
                                    <input
                                      type="text"
                                      name="kyc_address_3"
                                      id="buyer_kyc_address_3"
                                      value={userData.kyc_address_3}
                                      onChange={handleInputChange}
                                      placeholder={t('Complément adresse ligne 3')}
                                      aria-label={t("Complément adresse ligne 3")}

                                    />
                                  </div>
                                </div>
                              )}

                              {store.nbAddressLine >= 4 && (
                                <div className="formField" id="row_buyer_address_4">
                                  <div className="formControl">
                                    <input
                                      type="text"
                                      name="kyc_address_4"
                                      id="buyer_kyc_address_4"
                                      value={userData.kyc_address_4}
                                      onChange={handleInputChange}
                                      placeholder={t('Complément adresse ligne 4')}
                                      aria-label={t("Complément adresse ligne 4")}

                                    />
                                  </div>
                                </div>
                              )}

                              <div className="formField">
                                <div className="formControl">
                                  <input
                                    type="text"
                                    name="kyc_city"
                                    id="buyer_kyc_city"
                                    value={userData.kyc_city}
                                    onChange={handleInputChange}
                                    required={isKycFieldRequired(store.kycs?.buyer_address)}
                                    placeholder={t('Ville') + (isKycFieldRequired(store.kycs?.buyer_address) ? '*' : '')}
                                    aria-label={t("Ville")}
                                    className={formErrors.kyc_city ? 'error' : ''}
                                    aria-invalid={formErrors.kyc_city ? 'true' : 'false'}
                                    aria-describedby={formErrors.kyc_city ? 'error-kyc_city' : undefined}
                                    disabled={professionalUser && professionalUser.discount}
                                  />
                                </div>
                                {formErrors.kyc_city && <FieldError error={formErrors.kyc_city} id="kyc_city" />}
                              </div>

                              <div className="formField">
                                <div className="formControl">
                                  <input
                                    type="text"
                                    name="kyc_postalCode"
                                    id="buyer_kyc_postalCode"
                                    value={userData.kyc_postalCode}
                                    onChange={handleInputChange}
                                    required={isKycFieldRequired(store.kycs?.buyer_zipcode) || isKycFieldRequired(store.kycs?.buyer_address)}
                                    placeholder={t('Code postal') + ((isKycFieldRequired(store.kycs?.buyer_zipcode) || isKycFieldRequired(store.kycs?.buyer_address)) ? '*' : '')}
                                    aria-label={t("Code postal")}
                                    className={formErrors.kyc_postalCode ? 'error' : ''}
                                    aria-invalid={formErrors.kyc_postalCode ? 'true' : 'false'}
                                    aria-describedby={formErrors.kyc_postalCode ? 'error-kyc_postalCode' : undefined}
                                    disabled={professionalUser && professionalUser.discount}
                                  />
                                </div>
                                {formErrors.kyc_postalCode && <FieldError error={formErrors.kyc_postalCode} id="kyc_postalCode" />}
                              </div>

                              {store.isStateEnabled && (
                                <div className="formField">
                                  <div className="formControl">
                                    <select
                                      id="buyer_kyc_state"
                                      name="kyc_state"
                                      value={userData.kyc_state}
                                      onChange={handleInputChange}
                                    >
                                      <option value="">
                                        {store.physical_store?.texts?.state_list_label?.[lang]}
                                      </option>
                                      {store.physical_store?.states?.map(state => (
                                        <option key={state.code} value={state.code}>
                                          {getStateName(state)}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              )}

                              <div className="formField">
                                <div className="formControl">
                                  <input
                                    type="text"
                                    name="kyc_country"
                                    id="buyer_kyc_country"
                                    value={userData.kyc_country}
                                    onChange={handleInputChange}
                                    required={isKycFieldRequired(store.kycs?.buyer_address)}
                                    placeholder={t('Pays') + (isKycFieldRequired(store.kycs?.buyer_address) ? '*' : '')}
                                    aria-label={t("Pays")}
                                    className={formErrors.kyc_country ? 'error' : ''}
                                    aria-invalid={formErrors.kyc_country ? 'true' : 'false'}
                                    aria-describedby={formErrors.kyc_country ? 'error-kyc_country' : undefined}
                                    disabled={professionalUser && professionalUser.discount}
                                  />
                                </div>
                                {formErrors.kyc_country && <FieldError error={formErrors.kyc_country} id="kyc_country" />}
                              </div>
                            </>
                          )}

                          {/* KYC Civilité */}
                          {shouldShowKycField(store.kycs?.buyer_civility) && (
                            <div className="formField">
                              <div className="formControl">
                                <select
                                  id="buyer_kyc_civility"
                                  name="kyc_civility"
                                  value={userData.kyc_civility}
                                  onChange={handleInputChange}
                                  required={isKycFieldRequired(store.kycs?.buyer_civility)}
                                  className={formErrors.kyc_civility ? 'error' : ''}
                                  aria-invalid={formErrors.kyc_civility ? 'true' : 'false'}
                                  aria-describedby={formErrors.kyc_civility ? 'error-kyc_civility' : undefined}
                                  disabled={professionalUser && professionalUser.discount}
                                >
                                  <option value="">{t('Civilité') + (isKycFieldRequired(store.kycs?.buyer_civility) ? '*' : '')}</option>
                                  <option value="1">{t('Monsieur')}</option>
                                  <option value="2">{t('Madame')}</option>
                                  <option value="0">{t('Autre')}</option>
                                </select>
                              </div>
                              {formErrors.kyc_civility && <FieldError error={formErrors.kyc_civility} id="kyc_civility" />}
                            </div>
                          )}

                          {/* KYC Date de naissance */}
                          {shouldShowKycField(store.kycs?.buyer_birthdate) && (
                            <div className="formField">
                              <div className="formControl">
                                <DateInputWithMask
                                  name="kyc_birthdate"
                                  id="buyer_kyc_birthdate"
                                  value={userData.kyc_birthdate}
                                  onChange={handleInputChange}
                                  required={isKycFieldRequired(store.kycs?.buyer_birthdate)}
                                  aria-label={t("Date de naissance")}
                                  className={formErrors.kyc_birthdate ? 'error' : ''}
                                  aria-invalid={formErrors.kyc_birthdate ? 'true' : 'false'}
                                  aria-describedby={formErrors.kyc_birthdate ? 'error-kyc_birthdate' : undefined}
                                  disabled={professionalUser && professionalUser.discount}
                                />
                              </div>
                              {formErrors.kyc_birthdate && <FieldError error={formErrors.kyc_birthdate} id="kyc_birthdate" />}
                            </div>
                          )}


                          {/* TaxId via KYC (en dehors du bloc invoice) */}
                          {!store.isProSignboard && shouldShowKycField(store.kycs?.buyer_taxid) && store.sending_invoice_activated === 0 && (
                            <TaxId
                              store={store}
                              userData={userData}
                              setUserData={setUserData}
                              showInfo={handleShowInfo}
                              hideInfo={handleHideInfo}
                              infoField={infoField}
                              formErrors={formErrors}
                              setFormErrors={setFormErrors}
                              totalAmounts={totalAmounts}
                              isFromKycOnly={!store.codeTaxId && shouldShowKycField(store.kycs?.buyer_taxid)}
                            />
                          )}

                          <div className="rfOptinsCard">
                          {/* Facturation */}
                          {!store.isProSignboard && (store.sending_invoice_activated === 1 || store.sending_invoice_activated === 3) && (
                            <div className="formField">
                              <div className="formControl blockOptions customCheckbox">
                                <div className="optionItem">
                                  <input
                                    type="checkbox"
                                    name="isGettingInvoiceActivated"
                                    id="isGettingInvoiceActivated"
                                    checked={userData.isGettingInvoiceActivated}
                                    onChange={handleInputChange}
                                  />
                                  <label htmlFor="isGettingInvoiceActivated">
                                    {store.sending_invoice_activated === 1
                                      ? t('Je souhaite obtenir une facture')
                                      : store.sending_invoice_activated === 3
                                        ? <span dangerouslySetInnerHTML={{ __html: store.invoiceProfessionalTick?.[lang] }} />
                                        : null
                                    }
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}

                          {!store.isProSignboard && store.sending_invoice_activated > 0 &&
                            (store.sending_invoice_activated === 2 || userData.isGettingInvoiceActivated) && (
                              <>
                                {/* Champs TaxId et VAT selon leur position */}
                                {(store.tax_id_position === 1 || shouldShowKycField(store.kycs?.buyer_taxid)) && (
                                  <TaxId
                                    store={store}
                                    userData={userData}
                                    setUserData={setUserData}
                                    showInfo={handleShowInfo}
                                    hideInfo={handleHideInfo}
                                    infoField={infoField}
                                    formErrors={formErrors}
                                    setFormErrors={setFormErrors}
                                    totalAmounts={totalAmounts}
                                    isFromKycOnly={!store.codeTaxId && shouldShowKycField(store.kycs?.buyer_taxid)}
                                  />
                                )}

                                {store.vat_position === 1 && (
                                  <VAT
                                    store={store}
                                    userData={userData}
                                    setUserData={setUserData}
                                    showInfo={handleShowInfo}
                                    hideInfo={handleHideInfo}
                                    infoField={infoField}
                                    formErrors={formErrors}
                                    setFormErrors={setFormErrors}
                                  />
                                )}

                                {/* Champ Entreprise uniquement pour le cas 3 */}
                                {store.sending_invoice_activated === 3 && (
                                  <div className="formField">
                                    <div className="formControl">
                                      <input
                                        type="text"
                                        name="companyTransmitter"
                                        id="companyTransmitter"
                                        value={userData.companyTransmitter}
                                        onChange={handleInputChange}
                                        required
                                        placeholder={t('Entreprise') + '*'}
                                        aria-label={t("Entreprise")}
                                        className={formErrors.companyTransmitter ? 'error' : ''}
                                        aria-invalid={formErrors.companyTransmitter ? 'true' : 'false'}
                                        aria-describedby={formErrors.companyTransmitter ? 'error-companyTransmitter' : undefined}
                                        disabled={professionalUser && professionalUser.discount}
                                      />
                                    </div>
                                    {formErrors.companyTransmitter && <FieldError error={formErrors.companyTransmitter} id="companyTransmitter" />}
                                  </div>
                                )}

                                {/* Les champs d'adresse communs */}

                                <div className="formField">
                                  <div className="formControl">
                                    <input
                                      type="text"
                                      name="addressTransmitter"
                                      id="addressTransmitter"
                                      value={userData.addressTransmitter}
                                      onChange={handleInputChange}
                                      required
                                      placeholder={t('Adresse') +
                                        (store.sending_invoice_activated === 2 || userData.isGettingInvoiceActivated ? '*' : '')}
                                      aria-label={t("Adresse")}
                                      className={formErrors.addressTransmitter ? 'error' : ''}
                                      aria-invalid={formErrors.addressTransmitter ? 'true' : 'false'}
                                      aria-describedby={formErrors.addressTransmitter ? 'error-addressTransmitter' : undefined}
                                      disabled={professionalUser && professionalUser.discount}
                                    />
                                    <label className="flLabel" htmlFor="addressTransmitter" aria-hidden="true">{t('Adresse')}</label>
                                  </div>
                                  {formErrors.addressTransmitter && <FieldError error={formErrors.addressTransmitter} id="addressTransmitter" />}
                                </div>

                                <div className="formField">
                                  <div className="formControl">
                                    <input
                                      type="text"
                                      name="addressComplementTransmitter"
                                      id="addressComplementTransmitter"
                                      value={userData.addressComplementTransmitter || ''}
                                      onChange={handleInputChange}
                                      required
                                      placeholder={t("Complément d'adresse (Facultatif)")}
                                      aria-label={t("Complément d'adresse (Facultatif)")}
                                      className={formErrors?.addressComplementTransmitter ? 'error' : ''}
                                      aria-invalid={formErrors?.addressComplementTransmitter ? 'true' : 'false'}
                                      aria-describedby={formErrors?.addressComplementTransmitter ? 'error-addressComplementTransmitter' : undefined}
                                    />
                                    <label className="flLabel" htmlFor="addressComplementTransmitter" aria-hidden="true">{t("Complément d'adresse (Facultatif)")}</label>
                                  </div>
                                  {formErrors?.addressComplementTransmitter && (
                                    <div className="field-error" id="error-addressComplementTransmitter" role="alert">
                                      <span className="error-icon" aria-hidden="true">⚠️</span>
                                      <span className="error-message">{formErrors.addressComplementTransmitter}</span>
                                    </div>
                                  )}
                                </div>
                                {/* Adresses complémentaires selon nbAddressLine */}
                                {store.nbAddressLine >= 3 && (
                                  <div className="formField">
                                    <div className="formControl">
                                      <input
                                        type="text"
                                        name="addressComplementTransmitter1"
                                        id="addressComplementTransmitter1"
                                        value={userData.addressComplementTransmitter1}
                                        onChange={handleInputChange}
                                        placeholder={t('Complément adresse1')}
                                        aria-label={t("Complément adresse1")}

                                      />
                                    </div>
                                  </div>
                                )}

                                {store.nbAddressLine >= 4 && (
                                  <div className="formField">
                                    <div className="formControl">
                                      <input
                                        type="text"
                                        name="addressComplementTransmitter2"
                                        id="addressComplementTransmitter2"
                                        value={userData.addressComplementTransmitter2}
                                        onChange={handleInputChange}
                                        placeholder={t('Complément adresse2')}
                                        aria-label={t("Complément adresse2")}

                                      />
                                    </div>
                                  </div>
                                )}
                                <div className="fieldRow">
                                <div className="formField fieldPostal">
                                  <div className="formControl">
                                    <input
                                      type="text"
                                      name="postalCode"
                                      id="postalCode"
                                      value={userData.postalCode}
                                      onChange={handleInputChange}
                                      required
                                      placeholder={t('Code postal') +
                                        (store.sending_invoice_activated === 2 || userData.isGettingInvoiceActivated ? '*' : '')}
                                      aria-label={t("Code postal")}
                                      className={formErrors.postalCode ? 'error' : ''}
                                      aria-invalid={formErrors.postalCode ? 'true' : 'false'}
                                      aria-describedby={formErrors.postalCode ? 'error-postalCode' : undefined}
                                      disabled={professionalUser && professionalUser.discount}
                                    />
                                    <label className="flLabel" htmlFor="postalCode" aria-hidden="true">{t('Code postal')}</label>
                                  </div>
                                  {formErrors.postalCode && <FieldError error={formErrors.postalCode} id="postalCode" />}
                                </div>
                                <div className="formField">
                                  <div className="formControl">
                                    <input
                                      type="text"
                                      name="city"
                                      id="city"
                                      value={userData.city}
                                      onChange={handleInputChange}
                                      required
                                      placeholder={t('Ville') +
                                        (store.sending_invoice_activated === 2 || userData.isGettingInvoiceActivated ? '*' : '')}
                                      aria-label={t("Ville")}
                                      className={formErrors.city ? 'error' : ''}
                                      aria-invalid={formErrors.city ? 'true' : 'false'}
                                      aria-describedby={formErrors.city ? 'error-city' : undefined}
                                      disabled={professionalUser && professionalUser.discount}

                                    />
                                    <label className="flLabel" htmlFor="city" aria-hidden="true">{t('Ville')}</label>
                                  </div>
                                  {formErrors.city && <FieldError error={formErrors.city} id="city" />}
                                </div>
                                </div>
                                {/* Select pour state si activé */}
                                {store.isStateEnabled && (
                                  <div className="formField">
                                    <div className="formControl">
                                      <select
                                        id="state"
                                        name="state"
                                        value={userData.state}
                                        onChange={handleInputChange}
                                      >
                                        <option value="">
                                          {store.physical_store?.texts?.state_list_label?.[lang]}
                                        </option>
                                        {store.physical_store?.states?.map(state => (
                                          <option key={state.code} value={state.code}>
                                            {getStateName(state)}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                )}
                                <div className="formField">
                                  <div className="formControl">
                                    <input
                                      type="text"
                                      name="country"
                                      id="country"
                                      value={userData.country}
                                      onChange={handleInputChange}
                                      required
                                      placeholder={t('Pays') +
                                        (store.sending_invoice_activated === 2 || userData.isGettingInvoiceActivated ? '*' : '')}
                                      aria-label={t("Pays")}
                                      className={formErrors.country ? 'error' : ''}
                                      aria-invalid={formErrors.country ? 'true' : 'false'}
                                      aria-describedby={formErrors.country ? 'error-country' : undefined}
                                      disabled={professionalUser && professionalUser.discount}
                                    />
                                    <label className="flLabel" htmlFor="country" aria-hidden="true">{t('Pays')}</label>
                                  </div>
                                  {formErrors.country && <FieldError error={formErrors.country} id="country" />}
                                </div>
                              </>
                            )}
                          <div className="formField cgvBlock">
                            <div className="formField">
                              <div className="formControl blockOptions customCheckbox">
                                <div className="customCheckbox optionItem">
                                  <input
                                    type="checkbox"
                                    name="accepteCGV"
                                    id="accepteCGV"
                                    checked={userData.accepteCGV}
                                    onChange={handleInputChange}
                                    className={formErrors.accepteCGV ? 'error' : ''}
                                    aria-invalid={formErrors.accepteCGV ? 'true' : 'false'}
                                  />
                                  <label htmlFor="accepteCGV" className={formErrors.accepteCGV ? 'error-label' : ''}>
                                    <span className="optinTitle">{t('CGV & CGU')}</span>
                                    <span className="optinCaption">
                                    {t("J'accepte")}{' '}

                                    {/* Lien CGV */}
                                    {store.cgvExternalLink && store.cgvExternalLink[lang] && store.cgvExternalLink[lang] !== '' ? (
                                      <a
                                        href={store.cgvExternalLink[lang]}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mainColor"
                                        aria-label={store.fused_cgv_cgu ? t("les conditions générales de vente et d'utilisation") : t('les conditions générales de ventes')}
                                      >
                                        {store.fused_cgv_cgu ? t("les conditions générales de vente et d'utilisation") : t('les conditions générales de ventes')}
                                      </a>
                                    ) : (
                                        <a
                                          href={store.fused_cgv_cgu ? '/CGU-V' : '/CGV'}
                                          className="mainColor"
                                          aria-label={store.fused_cgv_cgu ? t("les conditions générales de vente et d'utilisation") : t('les conditions générales de ventes')}
                                        >
                                          {store.fused_cgv_cgu ? t("les conditions générales de vente et d'utilisation") : t('les conditions générales de ventes')}
                                        </a>
                                      )}

                                    {!store.fused_cgv_cgu && (
                                      <>
                                        {' '}{t('et')}{' '}
                                        {store.cguExternalLink && store.cguExternalLink[lang] && store.cguExternalLink[lang] !== '' ? (
                                          <a
                                            href={store.cguExternalLink[lang]}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mainColor"
                                            aria-label={t("les conditions générales d'utilisation")}
                                          >
                                            {t("les conditions générales d'utilisation")}
                                          </a>
                                        ) : (
                                            <a
                                              href="/CGU"
                                              className="mainColor"
                                              aria-label={t("les conditions générales d'utilisation")}
                                            >
                                              {t("les conditions générales d'utilisation")}
                                            </a>
                                          )}
                                      </>
                                    )}

                                    {' '}{t('de la carte cadeaux')} {store.libelle}*
                                    </span>
</label>
                                </div>
                              </div>

                              {/* Affichage des extraOptins */}
                              {store.extraOptins && Array.isArray(store.extraOptins) && store.extraOptins.map((option, index) => {
                                const optionText = option.text[lang] ||
                                  option.text[store.default_language] ||
                                  option.text[Object.keys(option.text)[0]];

                                return (
                                  <div key={option.key} className="formControl blockOptions customCheckbox">
                                    <div className="optionItem">
                                      <input
                                        type="checkbox"
                                        name={`extraOptin_${option.key}`}
                                        id={`extraOptin_${option.key}`}
                                        checked={userData.extraOptins[option.key] || false}
                                        onChange={handleInputChange}
                                        className={formErrors.extraOptinsRequired && option.required && !userData.extraOptins[option.key] ? 'error' : ''}
                                        aria-invalid={formErrors.extraOptinsRequired && option.required && !userData.extraOptins[option.key] ? 'true' : 'false'}
                                      />
                                      <label
                                        htmlFor={`extraOptin_${option.key}`}
                                        className={formErrors.extraOptinsRequired && option.required && !userData.extraOptins[option.key] ? 'error-label' : ''}

                                      >
                                        {stripHtml(optionText) + (option.required ? '*' : '')}
                                      </label>
                                    </div>
                                  </div>
                                );
                              })}

                              {store.opt_in && (
                                <div className="formControl blockOptions customCheckbox">
                                  <div className="optionItem">
                                    <input
                                      type="checkbox"
                                      name="accepteNL"
                                      id="userAccepteNL"
                                      checked={userData.accepteNL}
                                      onChange={handleInputChange}
                                    />
                                    <label htmlFor="userAccepteNL">
                                      {t("J'accepte de recevoir des informations de la part de")} {store.libelle}
                                    </label>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Messages d'erreur */}
                            <div className="fieldsGroup">
                              <div className="whitebox">
                                {t("Les champs marqués d'un astérisque (*) sont obligatoires")}
                              </div>
                              {Object.values(formErrors).some(error => error) && (
                                <div className="errorBox" role="alert" aria-live="polite">
                                  <div className="error-summary">
                                    <span className="error-icon" aria-hidden="true">⚠️</span>

                                    <span className="error-title">
                                      {formErrors.exceptionMsg
                                        ? t(formErrors.exceptionMsg)
                                        : t("Veuillez corriger les erreurs suivantes :")}
                                    </span>

                                    {!formErrors.exceptionMsg && (
                                      <ul>
                                        {Object.entries(formErrors).map(([key, message]) =>
                                          message ? <li key={key}>{message}</li> : null
                                        )}
                                      </ul>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>


                          {store.hasPromotionCode && (
                            <div className="promoCodeSection">
                              {promoDiscount ? (
                                <div className="promoCodeApplied">
                                  <span className="promoCodeSuccess">
                                    {t('Code appliqué')} : <strong>{promoCode}</strong>
                                    {cartDiscountCondition && (
                                      <span>
                                        {' '}({discountType === 'discount' ? '-' : '+'}{cartDiscountCondition.valueType === 'percent'
                                          ? `${cartDiscountCondition.value}%`
                                          : formatAmounts(cartDiscountCondition.value, 2)})
                                      </span>
                                    )}
                                  </span>
                                  <button
                                    type="button"
                                    className="rfBtn promoCodeRemoveBtn"
                                    onClick={() => {
                                      removePromoCode();
                                      setPromoCodeInput('');
                                    }}
                                  >
                                    {t('Retirer')}
                                  </button>
                                </div>
                              ) : (
                                <div className="promoCodeInputRow">
                                  <input
                                    type="text"
                                    className="promoCodeInput"
                                    placeholder={t('Code promotionnel')}
                                    value={promoCodeInput}
                                    onChange={(e) => setPromoCodeInput(e.target.value)}
                                    disabled={promoLoading}
                                    aria-label={t('Code promotionnel')}
                                  />
                                  <button
                                    type="button"
                                    className={`rfBtn sendOrder promoCodeBtn${!promoCodeInput.trim() || promoLoading ? ' promoCodeBtnDisabled' : ''}`}
                                    onClick={() => applyPromoCode(promoCodeInput)}
                                    disabled={!promoCodeInput.trim() || promoLoading}
                                  >
                                    {promoLoading ? t('Vérification...') : t('Ajouter')}
                                  </button>
                                </div>
                              )}
                              {promoError && (
                                <div className="promoCodeError">{t(promoError)}</div>
                              )}
                            </div>
                          )}

                          {store.PSP && store.PSP.toLowerCase().includes('stripe') && cart.items?.length > 0 && (
                            <div className="bankTransferInfoLink" style={{ textAlign: 'center', marginBottom: 10, cursor: 'pointer' }} onClick={handleShowBankTransferInfo}>
                              <span className="typoMedium" style={{ color: '#337ab7', textDecoration: 'underline' }}>
                                {t('Comment fonctionne le paiement par virement bancaire ?')} <i className="icon-info" aria-hidden="true"></i>
                              </span>
                            </div>
                          )}
                          <div className="actionsRow labelMargin">
                            {cart.items?.length > 0 && (
                              <Button
                                type="submit"
                                name="submit"
                                id="submitOrder"
                                className="sendOrder"
                                isLoading={orderLoading}
                              >
                                {t('Passer au paiement sécurisé')}
                              </Button>
                            )}
                            {cart.items?.length > 0 && (
                              <input
                                style={{ minWidth: '100%' }}
                                type="button"
                                name="buyOther"
                                id="buyOtherCard"
                                className="rfBtn buyOther"
                                onClick={() => goToStep(1)}
                                value={t('Acheter une autre carte')}
                              />
                            )}
                            {store.isAmexAvailable && cart.useAmex && (
                              <div className="amexText">
                                <span>{t('Je régle mon achat avec ma carte American Express')}</span>
                                <img src="assets/images/amex.jpg" className="amexLogo" alt="Amex" />
                              </div>
                            )}

                          </div>
                          </div>{/* /rfOptinsCard */}

                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </form>
            )}
            {step === 'payment' && (
              <div className="paymentHead">
                <div className="totalRow grandTotal">
                  <span>{t('Total')}</span>
                  <span>{formatAmounts(totalFinal, 2)}</span>
                </div>
              </div>
            )}
            <div style={{ display: step === "payment" ? 'block' : 'none' }} id="paymentHtml">
              {pspType === 'local-form' && paymentHtml && (
                <PaymentFormLocal htmlInfo={paymentHtml} />
              )}

            </div>
            {step === 'payment' && (
              <button
                type="button"
                className="cancelPaymentLink"
                onClick={() => goToStep(2)}
              >
                {t('Annuler ce paiement')}
              </button>
            )}
          </div>


          {/* Formulaire caché pour le mode redirect ou overlay */}
          {(store.PSP_TYPE === 'redirect' || store.PSP_TYPE === 'overlay') && (
            <form
              id="paymentForm"
              method="post"
              acceptCharset={store.PSP === 'Inicis' ? 'euc-kr' : undefined}
              noValidate
              aria-hidden="true"
              style={{ display: 'none' }}
            >
              <div className="paymentData">
                {Object.keys(store.PSP_fields || {}).map(key => (
                  <input
                    key={key}
                    id={key}
                    type="hidden"
                    name={key}
                  />
                ))}
              </div>
            </form>
          )}

      {/* Modal */}
      {showModal && (
        <Modal
          modalType={modalType}
          onHideModal={() => {
            setShowModal(false);
            setModalType(null);
            setItemToDelete(null);
          }}
          onDeleteItem={(key) => {
            deleteRequestCartItem(key);
            setShowModal(false);
            setModalType(null);
            setItemToDelete(null);
          }}
          itemToDelete={itemToDelete}
          store={store}
        />
      )}
      {modalUpdateOpen && (
        <UpdateCardModal
          card={cardToUpdate}
          index={cardIndex}
          store={store}
          mode={modalUpdateType}
          onClose={() => setModalUpdateOpen(false)}
        />
      )}

    </div>
  );
};


export default Step2;